/**
 * Split an error/message string into React nodes with http(s) URLs
 * rendered as clickable anchors. Used by the wizard submit-error
 * banners so backend-authored actionable messages (e.g. the leadgen
 * ToS error naming `https://www.facebook.com/ads/leadgen/tos`) become
 * one-click paths to resolution.
 *
 * Not a general-purpose markdown renderer — just URL detection. Keeps
 * the surface area small and avoids pulling in DOMPurify / marked.
 * Trailing punctuation that commonly follows URLs in prose is not
 * consumed into the link (period, comma, close-paren, semicolon,
 * exclaim, question mark).
 */
import React from 'react';

// Match http(s) URLs, greedy up to whitespace. The trailing-punct
// stripper below trims sentence-ending characters that got captured.
const URL_RE = /https?:\/\/[^\s]+/g;
const TRAILING_PUNCT_RE = /[.,;:!?)"']+$/;

export function linkifyUrls(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  URL_RE.lastIndex = 0;
  let key = 0;
  while ((match = URL_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    // Strip trailing punctuation that isn't part of the URL, push it
    // back into the plain-text tail.
    let url = match[0];
    let trailing = '';
    const trailMatch = TRAILING_PUNCT_RE.exec(url);
    if (trailMatch) {
      trailing = trailMatch[0];
      url = url.slice(0, url.length - trailing.length);
    }
    nodes.push(
      <a
        key={`lnk-${key++}`}
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="underline text-red-900 hover:text-red-700"
      >
        {url}
      </a>,
    );
    if (trailing) nodes.push(trailing);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes.length > 0 ? nodes : [text];
}
